import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '../index'
import { UserRole } from '../../types'

// Basic selectors (defensive for undefined state.auth, e.g. before store is ready or circular import)
export const selectCurrentUser = (state: RootState) => state.auth?.user ?? null
export const selectAuthToken = (state: RootState) => state.auth?.token ?? null
export const selectIsAuthenticated = (state: RootState) => state.auth?.isAuthenticated ?? false
export const selectAuthLoading = (state: RootState) => state.auth?.loading ?? false
export const selectAuthError = (state: RootState) => state.auth?.error ?? null
export const selectRegistrationPending = (state: RootState) => state.auth?.registrationPending ?? false
export const selectRegistrationChannel = (state: RootState) => state.auth?.registrationChannel ?? null
export const selectRegistrationDestinationHint = (state: RootState) => state.auth?.registrationDestinationHint ?? null
export const selectRegistrationNeedsEmail = (state: RootState) => state.auth?.registrationNeedsEmail ?? false
export const selectRegistrationNeedsTelegram = (state: RootState) => state.auth?.registrationNeedsTelegram ?? false
export const selectRegistrationTelegramDeepLink = (state: RootState) => state.auth?.registrationTelegramDeepLink ?? null
export const selectRegistrationPath = (state: RootState) => state.auth?.registrationPath ?? null
export const selectPendingRegistrationEmail = (state: RootState) => state.auth?.pendingRegistrationEmail ?? null
export const selectPendingRegistrationPhone = (state: RootState) => state.auth?.pendingRegistrationPhone ?? null

// User role
export const selectCurrentUserRole = createSelector(
    [selectCurrentUser],
    (user) => user?.role
)

// Permission checks
export const selectIsSuperAdmin = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.SUPERADMIN
)

export const selectIsAdmin = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.ADMIN || user?.role === UserRole.SUPERADMIN
)

export const selectIsMarker = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.MARKER
)

export const selectIsRegularUser = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.USER
)

export const selectCanManageTables = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.SUPERADMIN || user?.role === UserRole.ADMIN || user?.role === UserRole.MARKER
)

export const selectCanManageUsers = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.SUPERADMIN || user?.role === UserRole.ADMIN || user?.role === UserRole.MARKER
)

export const selectCanViewReports = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.SUPERADMIN || user?.role === UserRole.ADMIN || user?.role === UserRole.MARKER
)

export const selectCanViewAdminReports = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.SUPERADMIN || user?.role === UserRole.ADMIN
)

export const selectCanManageTariffs = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.SUPERADMIN || user?.role === UserRole.ADMIN
)

export const selectCanManageLoyalty = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.SUPERADMIN
)

export const selectCanManageAppSettings = createSelector(
    [selectCurrentUser],
    (user) => user?.role === UserRole.SUPERADMIN || user?.role === UserRole.ADMIN
)

// User info
export const selectCurrentUserId = createSelector(
    [selectCurrentUser],
    (user) => user?.id
)

export const selectCurrentUserName = createSelector(
    [selectCurrentUser],
    (user) => user?.fullName
)

export const selectCurrentUserEmail = createSelector(
    [selectCurrentUser],
    (user) => user?.email
)

// Auth state checks
export const selectIsLoggingIn = createSelector(
    [selectAuthLoading, selectIsAuthenticated],
    (loading, isAuth) => loading && !isAuth
)

export const selectHasAuthError = createSelector(
    [selectAuthError],
    (error) => error !== null
)